import { LoginConsts } from 'client/util/Constants';
import { OtpStatus, getOtpErrorMessage } from 'client/util/otpStatusMapper';
import { SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE } from 'client/util/Constants/smsOtpLoginSession';
import { get } from 'lodash';
import { pollFailureCondition, pollSuccessCondition } from 'client/util/verifyPushHelpers';
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import MfaRequiredError from 'client/services/response/MfaRequiredError';
import RequestError from 'client/services/response/RequestError';
import WebAuthnService from 'client/services/webAuthnService';
import api from 'client/api';
import poll from 'client/containers/Login/poll';

const mfaLabels = useMfaLabels();

const userApi = api.user;
const {
  pushStatusMessages,
  responseMessages,
} = LoginConsts;
const {
  mfaRequired,
  mfaUnavailableForPwdReset,
  passwordResetError,
} = responseMessages;

export default {
  async getDeviceCertCode(): Promise<any> {
    try {
      const res = await userApi.requestDeviceCode();
      return res.data.code;
    } catch {
      return null;
    }
  },

  /**
   * Remove properties with null values from info object to not
   * purposely send them in the payload
   * @param  {Object} info [flexible form object with possible null values]
   * @return {Object} [object used as 'payload' in userApi.resetPassword]
   */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  reformatInfo(info: any): any {
    return Object.entries(info).reduce((obj: any, [key, value]) => {
      const formattedObj = obj;
      if (value !== null) {
        formattedObj[key] = value;
      }
      return formattedObj;
    }, {}) as any;
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  resetPassword(code: string, info: any): Promise<any> {
    const formattedInfo = this.reformatInfo(info);
    const isSmsOtpVerify = Boolean(formattedInfo.sessionId);

    return userApi.resetPassword(code, formattedInfo)
      .catch((error: unknown) => {
        this.handlePasswordResetError(error, { smsOtpVerify: isSmsOtpVerify });
      });
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  sendOtp(code: string, info: { pwdResetToken: string }): Promise<object> {
    const formattedInfo = this.reformatInfo(info);

    return userApi.sendPasswordResetSmsOtp(code, formattedInfo)
      .then((response: any) => {
        const sessionId = get(response, 'data.sessionId');
        const statusCode = get(response, 'status', 200);
        const rawOtpStatus = get(response, 'data.status');
        const otpStatus = typeof rawOtpStatus === 'string'
          ? rawOtpStatus.toUpperCase()
          : rawOtpStatus;

        if (!sessionId) {
          throw new RequestError(statusCode, mfaLabels.sms_otp.error.send);
        }

        const isErrorStatus = otpStatus !== undefined
          && otpStatus !== OtpStatus.PENDING;

        if (isErrorStatus) {
          const errorMessage = getOtpErrorMessage(otpStatus);
          throw new RequestError(statusCode, errorMessage);
        }

        return response;
      })
      .catch((error: unknown) => {
        if (error instanceof RequestError) {
          throw error;
        }
        if (error instanceof Error) {
          const statusCode = get(error, 'response.status', 500);
          const errorMessage = get(error, 'response.data.message', mfaLabels.sms_otp.error.send);
          throw new RequestError(statusCode, errorMessage);
        }
        throw error;
      });
  },

  handlePasswordResetError(
    error: unknown,
    options: { smsOtpVerify?: boolean } = {},
  ): void {
    if (error instanceof Error) {
      const errorMessage = get(error, 'response.data.message', passwordResetError);
      const httpStatus = ((get(error, 'response.status') as unknown as number) || 500);

      // TODO: Determine if we can reuse `mfaRequired` error message for password
      // reset attempts with no available factors.
      // See https://github.com/TheJumpCloud/SI/blob/master/routes/webui/systemusers.js#L361-L369
      if (errorMessage === mfaRequired || errorMessage === mfaUnavailableForPwdReset) {
        const factors = get(error, 'response.data.factors', []);
        const challenge = get(error, 'response.data.challenge', undefined);
        const pwdResetToken = get(error, 'response.data.pwdResetToken', '');
        throw new MfaRequiredError(factors, challenge, pwdResetToken);
      }

      if (options.smsOtpVerify && httpStatus === 429) {
        throw new RequestError(
          429,
          mfaLabels.sms_otp.error.tooManyFailedAttempts,
          SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE,
        );
      }

      throw new RequestError(httpStatus, errorMessage);
    } else {
      throw error;
    }
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  verifyPushResetPassword(code: string, info: any): Promise<any> {
    const pollForResponse = () => new Promise((resolve, reject) => {
      const pollConfig = { delay: 1000 };

      poll(
        () => userApi.resetPassword(code, info)
          .then((response: { data: any}) => response.data)
          .catch((error: any) => {
            const errorMessageResponse = get(error, 'response.data.message');
            const status = get(error, 'response.status');
            const statusCode = get(error, 'response.data.status', '');
            const parsedMessage = get(pushStatusMessages, statusCode, '');
            const displayDuration = get(error, 'response.headers.retry-after');

            const errorMessage = parsedMessage
              || errorMessageResponse
              || passwordResetError;

            return {
              status, statusCode, displayDuration, errorMessage,
            };
          }),
        {
          successCondition: pollSuccessCondition,
          successHandler: resolve,
          failureCondition: pollFailureCondition,
          failureHandler: reject,
        },
        pollConfig,
      );
    });

    return pollForResponse()
      .catch((error: any) => {
        const errorMessage = get(error, 'errorMessage', passwordResetError);
        const status = get(error, 'status');
        const statusCode = get(error, 'statusCode');
        const displayDuration = get(error, 'displayDuration');

        throw new RequestError(status, errorMessage, statusCode, displayDuration);
      });
  },

  async verifyWebAuthnSignature(
    passwordResetInfo: PasswordResetInfo,
    challenge: Challenge,
  ): Promise<any> {
    try {
      const payload = await WebAuthnService.preparePasswordResetRequest(challenge);

      return await userApi.resetPassword(
        passwordResetInfo.confirmationCode,
        {
          ...passwordResetInfo,
          ...payload,
        },
      );
    } catch (error: unknown) {
      if (error instanceof RequestError) {
        // errors from WebAuthnService
        throw error;
      } else {
        // errors from userApi.resetPassword
        return this.handlePasswordResetError(error);
      }
    }
  },
};
